import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OrganizationSettings from '../components/settings/OrganizationSettings';
import OrganizationList from '../components/settings/OrganizationList';
import BillingSettings from '../components/settings/BillingSettings';
import SimpleUserManager from '../components/settings/SimpleUserManager';
import EmailTemplateManager from '../components/settings/EmailTemplateManager';
import EmailSettings from '../components/settings/EmailSettings';
import NotificationSettingsPage from './settings/NotificationSettings';
import AISettingsPage from './settings/AISettings';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../types/organization';
import { useLanguage } from '../contexts/LanguageContext';
import CommonPageHeader from '../components/common/CommonPageHeader';
import { 
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UsersIcon,
  DocumentTextIcon,
  CurrencyDollarIcon,
  ServerIcon,
  BellIcon,
  CpuChipIcon,
  Bars3Icon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const Settings = () => {
  const { user } = useAuth(); 
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'organizations' | 'users' | 'billing' | 'email-templates' | 'email-settings' | 'notifications' | 'ai-settings'>('organizations');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setSelectedOrg({
      ...updatedOrg,
      ownerId: selectedOrg?.ownerId || user?.id || ''
    });
  };

  const tabs = [
    {
      id: 'organizations' as const,
      name: t('settings.tabs.organizations.name'),
      icon: BuildingOfficeIcon,
      description: t('settings.tabs.organizations.description'),
      color: 'blue',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      activeColor: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500',
      roles: ['admin', 'super_admin', 'user']
    },
    {
      id: 'users' as const,
      name: t('settings.tabs.users.name'),
      icon: UsersIcon,
      description: t('settings.tabs.users.description'),
      color: 'purple',
      bgColor: 'bg-purple-100 dark:bg-purple-900/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
      activeColor: 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-l-2 border-purple-500',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'notifications' as const,
      name: t('settings.tabs.notifications.name'),
      icon: BellIcon,
      description: t('settings.tabs.notifications.description'),
      color: 'teal',
      bgColor: 'bg-teal-100 dark:bg-teal-900/20',
      iconColor: 'text-teal-600 dark:text-teal-400',
      activeColor: 'bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border-l-2 border-teal-500',
      roles: ['admin', 'super_admin', 'user']
    },
    {
      id: 'ai-settings' as const,
      name: t('settings.tabs.aiSettings.name'),
      icon: CpuChipIcon,
      description: t('settings.tabs.aiSettings.description'),
      color: 'indigo',
      bgColor: 'bg-indigo-100 dark:bg-indigo-900/20',
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      activeColor: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-l-2 border-indigo-500',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'email-settings' as const,
      name: t('settings.tabs.emailSettings.name'),
      icon: ServerIcon,
      description: t('settings.tabs.emailSettings.description'),
      color: 'green',
      bgColor: 'bg-green-100 dark:bg-green-900/20',
      iconColor: 'text-green-600 dark:text-green-400',
      activeColor: 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-l-2 border-green-500',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'email-templates' as const,
      name: t('settings.tabs.emailTemplates.name'),
      icon: DocumentTextIcon,
      description: t('settings.tabs.emailTemplates.description'),
      color: 'cyan',
      bgColor: 'bg-cyan-100 dark:bg-cyan-900/20',
      iconColor: 'text-cyan-600 dark:text-cyan-400',
      activeColor: 'bg-cyan-50 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 border-l-2 border-cyan-500',
      roles: ['admin', 'super_admin']
    },
    {
      id: 'billing' as const,
      name: t('settings.tabs.billing.name'),
      icon: CurrencyDollarIcon,
      description: t('settings.tabs.billing.description'),
      color: 'orange',
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      iconColor: 'text-orange-600 dark:text-orange-400',
      activeColor: 'bg-orange-50 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-l-2 border-orange-500',
      roles: ['admin', 'super_admin']
    }
  ];

  const getColorClasses = (color: string, isActive: boolean) => {
    
    const colors = {
      blue: isActive 
        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-blue-200 dark:hover:border-blue-600 hover:text-blue-600 dark:hover:text-blue-400',
      purple: isActive 
        ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-purple-200 dark:hover:border-purple-600 hover:text-purple-600 dark:hover:text-purple-400',
      teal: isActive 
        ? 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-700 text-teal-700 dark:text-teal-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-teal-200 dark:hover:border-teal-600 hover:text-teal-600 dark:hover:text-teal-400',
      green: isActive 
        ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700 text-green-700 dark:text-green-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-green-200 dark:hover:border-green-600 hover:text-green-600 dark:hover:text-green-400',
      indigo: isActive 
        ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-indigo-200 dark:hover:border-indigo-600 hover:text-indigo-600 dark:hover:text-indigo-400',
      orange: isActive 
        ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-700 text-orange-700 dark:text-orange-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-orange-200 dark:hover:border-orange-600 hover:text-orange-600 dark:hover:text-orange-400',
      emerald: isActive 
        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-emerald-200 dark:hover:border-emerald-600 hover:text-emerald-600 dark:hover:text-emerald-400',
      slate: isActive 
        ? 'bg-slate-50 dark:bg-slate-900/20 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-slate-200 dark:hover:border-slate-600 hover:text-slate-600 dark:hover:text-slate-400',
      red: isActive 
        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 text-red-700 dark:text-red-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-200 dark:hover:border-red-600 hover:text-red-600 dark:hover:text-red-400',
      gray: isActive 
        ? 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-gray-200 dark:hover:border-gray-600 hover:text-gray-600 dark:hover:text-gray-400',
      cyan: isActive 
        ? 'bg-cyan-50 dark:bg-cyan-900/20 border-cyan-200 dark:border-cyan-700 text-cyan-700 dark:text-cyan-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-cyan-200 dark:hover:border-cyan-600 hover:text-cyan-600 dark:hover:text-cyan-400',
      amber: isActive 
        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-amber-200 dark:hover:border-amber-600 hover:text-amber-600 dark:hover:text-amber-400',
      lime: isActive 
        ? 'bg-lime-50 dark:bg-lime-900/20 border-lime-200 dark:border-lime-700 text-lime-700 dark:text-lime-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-lime-200 dark:hover:border-lime-600 hover:text-lime-600 dark:hover:text-lime-400',
      pink: isActive 
        ? 'bg-pink-50 dark:bg-pink-900/20 border-pink-200 dark:border-pink-700 text-pink-700 dark:text-pink-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-pink-200 dark:hover:border-pink-600 hover:text-pink-600 dark:hover:text-pink-400',
      violet: isActive 
        ? 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-300' 
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-violet-200 dark:hover:border-violet-600 hover:text-violet-600 dark:hover:text-violet-400'
    };
    return colors[color as keyof typeof colors] || colors.blue;
  };

  const availableTabs = tabs.filter(tab => tab.roles.includes(user?.role || ''));

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as typeof activeTab);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <CommonPageHeader
        title={t('settings.title')}
        subtitle={t('settings.subtitle')}
        icon={Cog6ToothIcon}
        actionButton={
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        }
      />

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden pt-4 md:pt-0">
        {/* Sidebar - Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          fixed md:relative z-50 md:z-auto
          w-64 h-full md:h-auto flex-shrink-0 
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
        `}>
          <div className="p-3">
            {/* Mobile close button */}
            <div className="md:hidden flex justify-between items-center mb-4 p-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('settings.title')}
              </h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Compact header */}
            <div className="mb-4 p-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 rounded-lg text-white shadow-md">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-white/20 rounded-md backdrop-blur-sm">
                  <Cog6ToothIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t('settings.title')}</h3>
                  <p className="text-xs text-white/70">Szervezet beállítások</p>
                </div>
              </div>
            </div>
            <nav className="space-y-0.5">
              {availableTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <div key={tab.id} className="relative">
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className={`
                        w-full flex items-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-150 group relative
                        ${isActive
                          ? `${tab.activeColor} shadow-sm`
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                        }
                      `}
                    >
                      {/* Compact icon background */}
                      <div className={`
                        flex items-center justify-center w-7 h-7 rounded-md mr-2.5 transition-all duration-150
                        ${isActive ? tab.bgColor : 'bg-gray-100 dark:bg-gray-700 group-hover:' + tab.bgColor}
                      `}>
                        <Icon className={`
                          h-4 w-4 transition-all duration-150
                          ${isActive 
                            ? tab.iconColor
                            : 'text-gray-500 dark:text-gray-400 group-hover:' + tab.iconColor
                          }
                        `} />
                      </div>
                      
                      {/* Compact text content */}
                      <div className="text-left flex-1 min-w-0">
                        <div className={`font-medium truncate leading-tight ${isActive ? 'text-current' : ''}`}>
                          {tab.name}
                        </div>
                        <div className={`text-xs truncate leading-tight transition-colors duration-150 ${
                          isActive 
                            ? 'text-current opacity-60' 
                            : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                        }`}>
                          {tab.description}
                        </div>
                      </div>
                      
                      {/* Compact active indicator */}
                      {isActive && (
                        <div className={`w-0.5 h-6 rounded-full absolute right-1 ${tab.iconColor.replace('text-', 'bg-')}`} />
                      )}
                    </button>
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 w-full md:w-auto">
          <div className="p-6">

          {/* Organizations */}
          {activeTab === 'organizations' && (
            <OrganizationList />
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <SimpleUserManager onOrganizationUpdate={handleOrganizationUpdate} />
          )}

          {/* Email/SMTP Settings */}
          {activeTab === 'email-settings' && (
            <EmailSettings />
          )}

          {/* Email Templates */}
          {activeTab === 'email-templates' && (
            <EmailTemplateManager />
          )}

          {/* Billing Settings */}
          {activeTab === 'billing' && (
            <BillingSettings />
          )}


          {/* Notifications */}
          {activeTab === 'notifications' && (
            <NotificationSettingsPage embedded={true} />
          )}

          {/* AI Settings */}
          {activeTab === 'ai-settings' && (
            <AISettingsPage />
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 